import type { DefaultValues, FieldValues } from "react-hook-form"
import { FormProvider, useForm } from "react-hook-form"
import type { ReactElement, ReactNode, SubmitEvent } from "react"
import { useEffect, useRef } from "react"
import { toast } from "sonner"
import FormPageLeave from "./FormPageLeave"
import { handleError } from "./handleError"
import type { BaseQueryError } from "@/base"
import { useAutosave } from "@/base"
import { useT } from "@/i18n"

type Props<TValues extends FieldValues, Result> = {
  defaultValues: DefaultValues<TValues>
  resetKey?: string
  // Structural so composed actions work too, not just RTK mutation triggers
  action: (values: TValues) => { unwrap: () => Promise<Result> }
  successMessage?: string
  errorMessage?: string
  resetOnSuccess?: boolean
  onSuccess?: (result: Result) => Promise<void> | void
  pageLeave?: {
    autoSave: boolean
    confirmMessage?: string
  }
  children: ReactNode
  method?: "POST" | "GET"
  className?: string
}

// Overload: when onSuccess doesn't use the result, Result is unconstrained
export default function ApiFormWrapper<TValues extends FieldValues>(
  props: Omit<Props<TValues, unknown>, "onSuccess"> & {
    onSuccess?: () => Promise<void> | void
  },
): ReactElement

// Overload: when onSuccess uses the result, Result must match action
export default function ApiFormWrapper<TValues extends FieldValues, Result>(
  props: Props<TValues, Result>,
): ReactElement

export default function ApiFormWrapper<TValues extends FieldValues, Result>({
  defaultValues,
  resetKey,
  action,
  successMessage,
  errorMessage,
  resetOnSuccess = false,
  onSuccess,
  pageLeave,
  children,
  method = "POST",
  className,
}: Props<TValues, Result>) {
  const { t } = useT("common")
  const methods = useForm<TValues>({ defaultValues })
  const latestDefaultValuesRef = useRef(defaultValues)
  const {
    handleSubmit: makeHandleSubmit,
    reset,
    setError,
    clearErrors,
  } = methods

  // Guard: skip the next resetKey-triggered form reset when a save just
  // completed. The save's cache upsert or navigation changes resetKey,
  // but we don't want to overwrite the user's in-progress edits.
  const isSaveInFlightRef = useRef(false)

  useEffect(() => {
    latestDefaultValuesRef.current = defaultValues
  }, [defaultValues])
  useEffect(() => {
    if (isSaveInFlightRef.current) {
      isSaveInFlightRef.current = false

      return
    }
    methods.reset(latestDefaultValuesRef.current)
  }, [methods, resetKey])

  // Unified save – used by both manual submit and autosave.
  // `silent` suppresses toast and field-level error display (autosave).
  const saveForm = async (values: TValues, silent: boolean) => {
    isSaveInFlightRef.current = true

    // Only the request itself counts as a failed save. Everything after it has
    // already been written, and reporting those failures as a failed save would
    // invite a retry that writes the same thing twice.
    let result: Result
    try {
      result = await action(values).unwrap()
    } catch (error) {
      isSaveInFlightRef.current = false
      if (!silent) {
        // handleError reports whether it could turn the failure into field
        // messages. When it could not – a server error, or no response at all –
        // the user has no other signal that the save failed.
        if (!handleError({ setError })(error as BaseQueryError)) {
          toast.error(errorMessage ?? t("form.saveFailed"))
        }
      }

      throw error
    }

    if (resetOnSuccess && !silent) {
      reset()
    } else {
      // Update dirty baseline without changing field values – no focus loss
      methods.reset(methods.getValues(), { keepValues: true })
    }

    try {
      await onSuccess?.(result)
    } catch (error) {
      // Usually navigation. The save landed either way, so tell Sentry rather
      // than the user – and leave isSaveInFlightRef armed, since the cache echo
      // for the save that did happen is still coming.
      console.error("ApiFormWrapper: onSuccess failed after saving", error)
    }

    if (successMessage && !silent) {
      toast.success(successMessage)
    }
  }

  // PageLeave save – intentionally minimal: just persist, skip onSuccess
  // (which often navigates – you don't want that when the user is already
  // navigating away).
  const onPageLeaveSave = async (values: TValues) => {
    isSaveInFlightRef.current = true
    try {
      await action(values).unwrap()
    } catch (error) {
      isSaveInFlightRef.current = false

      throw error
    }
  }

  // Background autosave – fires on a debounced timer while the form is dirty
  const autoSave = !!pageLeave?.autoSave
  const { isDirty, isValid } = methods.formState

  const doAutoSave = async () => {
    await makeHandleSubmit((values: TValues) => saveForm(values, true))()
  }

  const { restart, cancel } = useAutosave(
    doAutoSave,
    autoSave && isDirty && isValid,
  )

  // Restart debounce timer on each form change
  useEffect(() => {
    if (!autoSave) {
      return
    }
    // eslint-disable-next-line react-hooks/incompatible-library -- watch() subscription is the react-hook-form pattern for change detection; React Compiler skipping this component is acceptable
    const sub = methods.watch(() => {
      restart()
    })

    return () => {
      sub.unsubscribe()
    }
  }, [methods, autoSave, restart])

  const onFormSubmit = (event: SubmitEvent) => {
    event.preventDefault()
    event.stopPropagation()
    cancel()
    clearErrors()
    // saveForm rethrows for the autosave and page-leave callers, which need the
    // rejection to know the save did not land. Here it has already been shown
    // to the user, so swallow it rather than leave an unhandled rejection.
    makeHandleSubmit((values: TValues) => saveForm(values, false))().catch(
      () => undefined,
    )
  }

  return (
    <FormProvider {...methods}>
      <form method={method} onSubmit={onFormSubmit} className={className}>
        {pageLeave && (
          <FormPageLeave<TValues>
            onSubmit={onPageLeaveSave}
            autoSave={pageLeave.autoSave}
            confirmMessage={pageLeave.confirmMessage}
          />
        )}
        {children}
      </form>
    </FormProvider>
  )
}
